import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabaseClient } from "../../lib/supabase";
import { createSerpTask, analyzeSerpResults } from "../../lib/dataforseo";
import { LoadingScreen } from "../common/LoadingScreen";
import { Search, MapPin, List, Globe, ArrowRight, Save, Play, Database } from "lucide-react";
import { parseCompetitorContent } from "../../lib/content-parser";
import { addToQueue } from "../../lib/analysis-queue";
import { toast } from "../../lib/toast";

interface DataForSEOResult {
  url: string;
  title: string;
  description: string;
  position: number;
  domain: string;
  breadcrumb: string;
  links_count: number;
  main_domain: string;
  relative_url: string;
  etv: number;
  estimated_paid_traffic_cost: number;
  rank_group: number;
  xpath: string;
}

interface Location {
  id: string;
  name: string;
  parentId?: string;
  countryCode: string;
  type: 'Country' | 'Region';
}

const LOCATIONS: Location[] = [
  { id: "2804", name: "Ukraine", countryCode: "UA", type: "Country" },
  { id: "21118", name: "Kyiv city, Ukraine", parentId: "2804", countryCode: "UA", type: "Region" },
  { id: "21580", name: "Kyiv Oblast, Ukraine", parentId: "2804", countryCode: "UA", type: "Region" },
  { id: "21167", name: "United States", countryCode: "US", type: "Country" },
  { id: "21203", name: "Canada", countryCode: "CA", type: "Country" },
  { id: "2826", name: "United Kingdom", countryCode: "GB", type: "Country" }

];

const LANGUAGES = [
  { code: "uk", name: "Ukrainian" },
  { code: "en", name: "English" },
  { code: "ru", name: "Russian" }
];

export default function AnalysisCreate() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [keyword, setKeyword] = useState("");
  const [ownUrl, setOwnUrl] = useState("");
  const [location, setLocation] = useState("2804"); // Default to Ukraine
  const [language, setLanguage] = useState("uk"); // Default to Ukrainian
  const [keywordsList, setKeywordsList] = useState("");
  const [competitors, setCompetitors] = useState<DataForSEOResult[]>([]);
  const [step, setStep] = useState<'input' | 'processing' | 'results'>('input');
  const [progress, setProgress] = useState<string>("");
  const [difficultyScore, setDifficultyScore] = useState<number | null>(null);
  const [additionalKeywords, setAdditionalKeywords] = useState<string[]>([]);
  const [contentAnalysis, setContentAnalysis] = useState<any>(null);
  const [isAnalyzingContent, setIsAnalyzingContent] = useState(false);
  const [addToQueueAfterSave, setAddToQueueAfterSave] = useState(true);

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !keyword.trim()) {
      setError("Name and keyword are required");
      return;
    }

    setLoading(true);
    setError(null);
    setStep('processing');
    setProgress("Step 1/3: Creating SERP analysis task...");

    try {
      // Step 1: Create SERP task and get results immediately
      const results = await createSerpTask(keyword.trim(), location, language);
      
      // Calculate difficulty score
      setProgress("Step 1/3: Analyzing SERP results...");
      const score = await analyzeSerpResults(results);
      
      setCompetitors(results);
      setDifficultyScore(score);
      
      // Step 2: Automatically start content analysis
      setProgress("Step 2/3: Parsing competitor content...");
      const competitorsList = results.map(comp => ({
        domain: comp.domain,
        url: comp.url
      }));

      const analysis = await parseCompetitorContent(
        competitorsList,
        ownUrl || null,
        (progress) => setProgress(`Step 2/3: ${progress}`)
      );

      setContentAnalysis(analysis);
      
      // Step 3: Automatically save the analysis
      setProgress("Step 3/3: Saving analysis...");
      const analysisData = {
        project_id: projectId,
        name: name.trim(),
        keyword: keyword.trim(),
        quick_score: score,
        status: 'completed',
        status_type: 'task is ready',
        serp_results: results,
        additional_keywords: keywordsList.split('\n').filter(k => k.trim()),
        url: ownUrl.trim() || null,
        content_analysis: analysis,
        language: language,
        location: location
      };

      console.log('Saving analysis with data:', {
        ...analysisData,
        serp_results: `${results.length} results`,
        content_analysis: analysis ? 'Present' : 'Missing'
      });

      const { data, error: createError } = await supabaseClient
        .from("analyses")
        .insert(analysisData)
        .select()
        .single();

      if (createError) {
        console.error("Error creating analysis:", createError);
        throw createError;
      }

      console.log('Analysis saved successfully:', {
        id: data.id,
        serp_results_count: data.serp_results?.length || 0,
        content_analysis_present: !!data.content_analysis
      });
      
      // Add to queue if option is selected
      if (addToQueueAfterSave && data) {
        try {
          setProgress("Adding to processing queue...");
          await addToQueue(data.id);
          toast.success('Analysis created and added to processing queue');
        } catch (queueError) {
          console.error('Error adding to queue:', queueError);
          toast.success('Analysis created successfully');
        }
      } else {
        toast.success('Analysis created successfully');
      }
      
      // Wait a bit to ensure data is fully committed to the database
      setProgress("Finalizing...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate to analysis page
      navigate(`/analyses/${data.id}`);
      
    } catch (err) {
      console.error("Error analyzing keyword:", err);
      setError(err instanceof Error ? err.message : "Failed to analyze keyword");
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleContentAnalysis = async () => {
    if (!competitors.length) return;

    setIsAnalyzingContent(true);
    setError(null);

    try {
      // Convert competitors to the format expected by parseCompetitorContent
      const competitorsList = competitors.map(comp => ({
        domain: comp.domain,
        url: comp.url
      }));

      const analysis = await parseCompetitorContent(
        competitorsList,
        ownUrl || null,
        (progress) => setProgress(progress)
      );

      setContentAnalysis(analysis);
      toast.success('Content analysis completed');
    } catch (err) {
      console.error("Error analyzing content:", err);
      setError(err instanceof Error ? err.message : "Failed to analyze content");
    } finally {
      setIsAnalyzingContent(false);
    }
  };

  const handleSave = async () => {
    if (!competitors.length || difficultyScore === null) return;

    setLoading(true);
    setError(null);

    try {
      // Create analysis record with SERP results and additional data
      const analysisData = {
        project_id: projectId,
        name: name.trim(),
        keyword: keyword.trim(),
        quick_score: difficultyScore,
        status: 'completed',
        status_type: 'task is ready',
        serp_results: competitors,
        additional_keywords: keywordsList.split('\n').filter(k => k.trim()),
        url: ownUrl.trim() || null,
        content_analysis: contentAnalysis,
        language: language,
        location: location
      };

      console.log('Saving analysis:', analysisData);

      const { data, error: createError } = await supabaseClient
        .from("analyses")
        .insert(analysisData)
        .select()
        .single();

      if (createError) {
        console.error("Error creating analysis:", createError);
        throw createError;
      }

      console.log('Analysis saved:', data);
      
      // Add to queue if option is selected
      if (addToQueueAfterSave && data) {
        try {
          await addToQueue(data.id);
          toast.success('Analysis saved and added to processing queue');
        } catch (queueError) {
          console.error('Error adding to queue:', queueError);
          toast.success('Analysis saved successfully');
        }
      } else {
        toast.success('Analysis saved successfully');
      }
      
      // Navigate to analysis page
      navigate(`/analyses/${data.id}`);
    } catch (err) {
      console.error("Error saving analysis:", err);
      setError(err instanceof Error ? err.message : "Failed to save analysis");
    } finally {
      setLoading(false);
    }
  };

  if (step === 'processing') {
    // Calculate progress percentage based on current step
    const getProgressPercentage = () => {
      if (progress.includes('Step 1/3')) return 33;
      if (progress.includes('Step 2/3')) {
        // More detailed progress for step 2 based on competitor parsing
        if (progress.includes('competitor')) {
          const match = progress.match(/competitor (\d+) of (\d+)/);
          if (match) {
            const current = parseInt(match[1]);
            const total = parseInt(match[2]);
            return 33 + ((current / total) * 33);
          }
        }
        return 66;
      }
      if (progress.includes('Step 3/3')) return 100;
      return 0;
    };

    const progressPercentage = getProgressPercentage();

    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              <div className={`flex-1 text-center ${progress.includes('Step 1/3') ? 'text-blue-600 font-semibold' : progress.includes('Step 2/3') || progress.includes('Step 3/3') ? 'text-green-600' : 'text-gray-400'}`}>
                <div className="text-sm">Step 1</div>
                <div className="text-xs">SERP Analysis</div>
              </div>
              <div className={`flex-1 text-center ${progress.includes('Step 2/3') ? 'text-blue-600 font-semibold' : progress.includes('Step 3/3') ? 'text-green-600' : 'text-gray-400'}`}>
                <div className="text-sm">Step 2</div>
                <div className="text-xs">Content Parsing</div>
              </div>
              <div className={`flex-1 text-center ${progress.includes('Step 3/3') ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                <div className="text-sm">Step 3</div>
                <div className="text-xs">Saving</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="mt-1 text-xs text-gray-500 text-right">
              {Math.round(progressPercentage)}%
            </div>
          </div>

          {/* Loading Animation */}
          <div className="flex flex-col items-center">
            <LoadingScreen />
            <p className="mt-4 text-base text-gray-700 font-medium">{progress}</p>
            
            {/* Additional detailed info */}
            <div className="mt-3 text-sm text-gray-500 text-center max-w-md">
              {progress.includes('Creating SERP analysis') && (
                <p>⏳ Fetching TOP-10 search results from DataForSEO...</p>
              )}
              {progress.includes('Analyzing SERP results') && (
                <p>📊 Calculating difficulty score based on competitor metrics...</p>
              )}
              {progress.includes('Starting content analysis') && (
                <p>🚀 Initializing content parser for competitor analysis...</p>
              )}
              {progress.includes('Analyzing our content') && (
                <p>🏠 Parsing your own URL content...</p>
              )}
              {progress.includes('Analyzing competitor') && (
                <p>🔍 Extracting headers, text, and structure from competitor pages...</p>
              )}
              {progress.includes('Content analysis completed') && (
                <p>✅ Successfully parsed all competitor content!</p>
              )}
              {progress.includes('Saving analysis') && (
                <p>💾 Writing data to database...</p>
              )}
              {progress.includes('Adding to processing queue') && (
                <p>📋 Adding to AI processing queue...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'results') {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Analysis Results</h2>
            <div className="flex gap-2">
              {!isAnalyzingContent && !contentAnalysis && (
                <button
                  onClick={handleContentAnalysis}
                  disabled={isAnalyzingContent}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Content Analysis
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Analysis
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
              {error}
            </div>
          )}

          {isAnalyzingContent && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="mr-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                </div>
                <p className="text-sm text-blue-700">{progress}</p>
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Name: {name}</h3>
                  <p className="text-sm text-gray-500">
                    Keyword: {keyword}
                  </p>
                  <p className="text-sm text-gray-500">
                    Location: {LOCATIONS.find(l => l.id === location)?.name}
                  </p>
                </div>
                {difficultyScore !== null && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Difficulty Score</p>
                    <p className={`text-2xl font-bold ${
                      difficultyScore < 30 ? 'text-green-600' :
                      difficultyScore < 60 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {difficultyScore}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={addToQueueAfterSave}
                onChange={(e) => setAddToQueueAfterSave(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Add to processing queue after saving
              </span>
            </label>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">TOP-10 Results</h3>
            {competitors.length > 0 ? (
              competitors.map((result, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-500">{index + 1}#{result.position}</span>
                        <h4 className="text-lg font-medium text-blue-600 hover:text-blue-800">
                          <a href={result.url} target="_blank" rel="noopener noreferrer">
                            {result.title}
                          </a>
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{result.description}</p>
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-1" />
                          {result.domain}
                        </div>
                        <div>
                          Links: {result.links_count || 0}
                        </div>
                        {result.estimated_paid_traffic_cost > 0 && (
                          <div className="text-green-600">
                            Est. Traffic Cost: ${result.estimated_paid_traffic_cost}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No results found for this keyword.</p>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={() => setStep('input')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Back to Input
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {loading ? "Saving..." : "Save Analysis"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Create New Analysis</h2>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleAnalyze} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter analysis name"
              required
            />
          </div>

          <div>
            <label htmlFor="keyword" className="block text-sm font-medium text-gray-700">
              Target Keyword *
            </label>
            <div className="mt-1 relative">
              <input
                type="text"
                id="keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter your target keyword"
                required
              />
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <div className="mt-1 relative">
                <select
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                >
                  {LOCATIONS.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
                <MapPin className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                Language
              </label>
              <div className="mt-1 relative">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                <Globe className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="keywordsList" className="block text-sm font-medium text-gray-700">
              Additional Keywords (Optional)
            </label>
            <div className="mt-1 relative">
              <textarea
                id="keywordsList"
                value={keywordsList}
                onChange={(e) => setKeywordsList(e.target.value)}
                rows={4}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter additional keywords (one per line)"
              />
              <List className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Enter additional keywords to analyze, one per line
            </p>
          </div>

          <div>
            <label htmlFor="ownUrl" className="block text-sm font-medium text-gray-700">
              Your URL (Optional)
            </label>
            <input
              type="url"
              id="ownUrl"
              value={ownUrl}
              onChange={(e) => setOwnUrl(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="https://your-site.com/page"
            />
            <p className="mt-1 text-sm text-gray-500">
              Add your page URL to compare it with competitors
            </p>
          </div>

          <div className="mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={addToQueueAfterSave}
                onChange={(e) => setAddToQueueAfterSave(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Add to processing queue after saving
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(`/projects/show/${projectId}`)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              {loading ? "Processing..." : "Analyze Keyword"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { AnalysisCreate };